import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatBRL(value: number | string): string {
  const num = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
}

export const API_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';
export const WS_URL: string = import.meta.env.VITE_WS_URL ?? '/ws';

// =====================  Datas / Períodos  =====================

export interface DateRange {
  from: string; // yyyy-MM-dd
  to: string;   // yyyy-MM-dd
}

const MONTH_NAMES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** Data atual normalizada para meia-noite local. */
function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Formata uma Date como yyyy-MM-dd interpretando como data local — sem deslocamento de timezone. */
export function formatYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Intervalo (yyyy-MM-dd) cobrindo o mês informado. month é 0-based (0=jan). */
export function monthRange(year: number, month: number): DateRange {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0); // último dia do mês
  return { from: formatYmd(start), to: formatYmd(end) };
}

export function currentMonthRange(): DateRange {
  const t = today();
  return monthRange(t.getFullYear(), t.getMonth());
}

export function previousMonthRange(): DateRange {
  const t = today();
  return monthRange(t.getFullYear(), t.getMonth() - 1);
}

export function nextMonthRange(): DateRange {
  const t = today();
  return monthRange(t.getFullYear(), t.getMonth() + 1);
}

/** Rótulo do range. Se cobre um mês inteiro, retorna "Maio/2026"; senão "DD/MM/YYYY — DD/MM/YYYY". */
export function rangeLabel(range: DateRange): string {
  const [fy, fm, fd] = range.from.split('-').map(Number);
  const [ty, tm, td] = range.to.split('-').map(Number);
  const lastDayOfMonth = new Date(ty, tm, 0).getDate();
  const fullMonth = fy === ty && fm === tm && fd === 1 && td === lastDayOfMonth;
  if (fullMonth) return `${MONTH_NAMES_PT[fm - 1]}/${fy}`;
  return `${String(fd).padStart(2, '0')}/${String(fm).padStart(2, '0')}/${fy} — ${String(td).padStart(2, '0')}/${String(tm).padStart(2, '0')}/${ty}`;
}

export function rangeEquals(a: DateRange, b: DateRange): boolean {
  return a.from === b.from && a.to === b.to;
}
